import Button from "@components/common/Button";
import ListVesselsFilter from "@components/common/FilterDropdown.jsx";
import BusinessUnitsFilter from "@components/common/Filters/BusinessUnitsFilter.jsx";
import ClientsFilter from "@components/common/Filters/ClientsFilter.jsx";
import DistributorsFilter from "@components/common/Filters/DistributorsFilter.jsx";
import StaticSelectFilter from "@components/common/Filters/StaticSelectFilter.jsx";
import ListTable from "@components/common/ListTable";
import ModulesHeader from "@components/common/ModulesHeader";
import PageSizeSelector from "@components/common/PageSizeSelector.jsx";
import Pagination from "@components/common/pagination";
import SearchInput from "@components/common/SearchInput";
import FileCsvIcon from "@components/icons/FileCsvIcon";
import PlusIcon from "@components/icons/PlusIcon";
import { useIsMobile, useIsTablet } from "@components/mediaQuery/useIsMobile";
import { useFeatureValue } from "@growthbook/growthbook-react";
import { useDropdownFilters } from "@hooks/useDropdownFilters.js";
import {
	deleteVesselById,
	fetchDataVessels,
	setDownloadVesselsData,
} from "@services/vesselService";
import { toggleVesselsFiltersVisibility } from "@store/modules/vesselsSlice.js";
import FILTERS_KEYS from "@utils/filters_keys.js";
import { checkPermission } from "@utils/permissions";
import {
	IS_ACTIVATION_ROLE,
	IS_ADMIN_ROLE,
	IS_CUSTOMER_READONLY_ROLE,
	IS_CUSTOMER_ROLE,
	IS_DISTRIBUTOR_CUSTOMER_ADMIN_ROLE,
	IS_DISTRIBUTOR_CUSTOMER_OPERATOR_ROLE,
	IS_DISTRIBUTOR_CUSTOMER_READONLY_ROLE,
	IS_RESELLER_CUSTOMER_READONLY_ROLE,
	IS_RESELLER_CUSTOMER_ROLE,
} from "@utils/user_roles";
import { FLAGS } from "config/growthbook/flags.js";
import { Tooltip } from "flowbite-react";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

const statusOptions = [
	{
		label: "ONLINE",
		value: 1,
	},
	{
		label: "OFFLINE",
		value: 0,
	},
];

const useFetchVessels = (searchTerms, localPerPage, sortConfig, token, dispatch, navigate) => {
	useEffect(() => {
		const controller = new AbortController();
		const signal = controller.signal;

		const fetchVessels = debounce(() => {
			fetchDataVessels(
				dispatch,
				token,
				navigate,
				1,
				localPerPage,
				1,
				searchTerms,
				sortConfig.column,
				sortConfig.order,
				signal,
			);
		}, 300);

		fetchVessels();
		return () => {
			controller.abort();
			fetchVessels.cancel();
		};
	}, [searchTerms, sortConfig, token, dispatch, navigate, localPerPage]);
};

const Vessels = () => {
	const { t } = useTranslation("vessels");
	const controller = new AbortController();
	const signal = controller.signal;
	const [searchTerms, setSearchTerms] = useState({
		search: "",
		businessUnit: null,
		company_id: null,
		clientId: null,
	});
	const [sortConfig, setSortConfig] = useState({
		column: "",
		order: "",
	});

	// New filter states
	const [businessUnit, setBusinessUnit] = useState(null);
	const [distributor, setDistributor] = useState(null);
	const [client, setClient] = useState(null);
	const [status, setStatus] = useState(null);

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { token, permissions, business_units } = useSelector((state) => state.auth.user);
	const isMobile = useIsMobile();
	const isTablet = useIsTablet();

	const [isLoadingDownload, setIsLoadingDownload] = useState(false);
	const [isSuccess, setIsSuccess] = useState(false);
	const [hasError, setHasError] = useState(false);
	const [errorMessage, setErrorMessage] = useState(null); // Permissions
	const vesselsSite = permissions.find((resource) => resource.resource_name === "vessels_site");
	const accountManagement = permissions.find(
		(resource) => resource.resource_name === "account_management",
	);
	const distributors = accountManagement?.sub_resources?.find(
		(subResource) => subResource.sub_resource_name === "account_management_distributors",
	);
	const authorizedPermissionsDistributors = distributors?.permissions?.filter(
		(permission) => permission.authorized === true,
	);

	const canViewDistributors = authorizedPermissionsDistributors?.some(
		(permission) => permission.name === "view distributors",
	);

	const authorizedPermissions = vesselsSite?.permissions || [];
	const canCreateVessels = checkPermission(authorizedPermissions, "create vessels_site");
	const canCreateMSS = checkPermission(authorizedPermissions, "create mss");
	const canEditVessels = checkPermission(authorizedPermissions, "update vessels_site");
	const canDeleteVessels = checkPermission(authorizedPermissions, "delete vessels_site");

	const {
		data: vesselsData,
		loading,
		pagination,
		filters,
	} = useSelector((state) => state.modules.vessels);

	const { page: currentPage, perPage, totalItems, totalPage } = pagination || {};

	const [localPerPage, setLocalPerPage] = useState(perPage ?? 10);
	const userroleId = useSelector((state) => state.auth.user.role?.id);
	const roleParentId = useSelector((state) => state.auth.user.role.parentId);
	const isOptInColumnVisible = useFeatureValue(FLAGS.vessels.list.optInColumnVisible);

	// Permission checks
	const canViewClients =
		userroleId !== IS_DISTRIBUTOR_CUSTOMER_ADMIN_ROLE &&
		userroleId !== IS_DISTRIBUTOR_CUSTOMER_OPERATOR_ROLE &&
		userroleId !== IS_DISTRIBUTOR_CUSTOMER_READONLY_ROLE &&
		userroleId !== IS_RESELLER_CUSTOMER_ROLE &&
		userroleId !== IS_RESELLER_CUSTOMER_READONLY_ROLE &&
		userroleId !== IS_CUSTOMER_ROLE &&
		userroleId !== IS_CUSTOMER_READONLY_ROLE;

	const canViewBusinessUnitFilter =
		userroleId === IS_ADMIN_ROLE ||
		roleParentId === IS_ADMIN_ROLE ||
		userroleId === IS_ACTIVATION_ROLE ||
		roleParentId === IS_ACTIVATION_ROLE;

	const { vessel } = useFeatureValue(FLAGS.shared.vessels.titlesConfig, {
		vessel: "Vessels/Sites",
	});

	// Fetch vessels
	useFetchVessels(searchTerms, localPerPage, sortConfig, token, dispatch, navigate);

	// Filter handlers
	const handleBusinessUnitChange = (value) => {
		setBusinessUnit(value);
		setDistributor(null);
		setClient(null);
		setSearchTerms((prev) => ({
			...prev,
			businessUnit: value?.id || null,
			company_id: null,
			clientId: null,
		}));
	};

	const handleDistributorChange = (value) => {
		setDistributor(value);
		setClient(null);
		setSearchTerms((prev) => ({
			...prev,
			company_id: value?.id || null,
			clientId: null,
		}));
	};

	const handleClientChange = (value) => {
		setClient(value);
		setSearchTerms((prev) => ({
			...prev,
			clientId: value?.id || null,
		}));
	};

	const handleStatusChange = (value) => {
		setStatus(value);
		setSearchTerms((prev) => {
			const updated = { ...prev };
			if (value === null || value === undefined) {
				delete updated.status;
			} else {
				updated.status = value.value;
			}
			return updated;
		});
	};

	const handleSearchChange = useCallback((id, value) => {
		setSearchTerms((prevTerms) => ({ ...prevTerms, [id]: value }));
	}, []);

	const startItemIndex = useMemo(() => (currentPage - 1) * perPage + 1, [currentPage, perPage]);
	const endItemIndex = useMemo(
		() => Math.min(currentPage * perPage, totalItems),
		[currentPage, perPage, totalItems],
	);

	const columns = useMemo(
		() => [
			{
				header: "SITE NAME",
				field: "site_name",
				sortable: true,
				content: (rowData) => {
					return <Link to={`/vessel/${rowData?.id}`}>{rowData?.site_name}</Link>;
				},
			},
			{
				header: "CLIENT",
				field: "client.name",
				sortable: true,
			},
			{
				header: "STATUS",
				field: "status",
				sortable: false,
				content: (data) => (
					<span
						className={`me-1 rounded px-2 py-0.5 font-medium text-[10px] sm:me-2 sm:px-2.5 sm:py-0.5 sm:text-xs ${
							data.status === 1
								? "border border-green-400 bg-green-dark text-green-400"
								: "border border-red-400 bg-red-dark text-red-400"
						}`}
					>
						{data.status === 1 ? "ONLINE" : "OFFLINE"}
					</span>
				),
			},
			{
				header: "Plan (GB)",
				field: "plan",
				sortable: false,
			},
			{
				header: "Total usage (GB)",
				field: "total_usage",
				sortable: false,
			},

			...(isOptInColumnVisible
				? [
						{
							header: "Credit limit option",
							field: "starlinks[0].credit_limit.current_month_option",
							sortable: false,
							content: (data) => (
								<span>{data?.starlinks?.[0]?.credit_limit?.current_month_option ?? "-"}</span>
							),
						},
					]
				: []),
		],
		[isMobile, isTablet, isOptInColumnVisible],
	);

	const handleDeleteVessel = async (id) => {
		try {
			const result = await deleteVesselById(id, token, dispatch, navigate, currentPage, perPage);

			if (result.status === "success") {
				setTimeout(() => {
					let current_page =
						vesselsData && vesselsData.length === 1 ? currentPage - 1 : currentPage;
					fetchDataVessels(
						dispatch,
						token,
						navigate,
						current_page,
						perPage,
						1,
						searchTerms,
						sortConfig.column,
						sortConfig.order,
					);
				}, 1000);
				return true;
			}
		} catch (error) {
			console.error("Error deleting vessel:", error);
			throw error;
		}
	};

	const disableDeleteCondition = (row) => {
		return row.smartboxes?.length > 0 || row.starlinks?.length > 0;
	};

	const handleRowClickWithCtrl = (id, name, event) => {
		if (event.ctrlKey || event.metaKey) {
			window.open(`/vessel/${id}`, "_blank");
		} else {
			navigate(`/vessel/${id}`);
		}
	};

	const fetchSortedData = (column) => {
		setSortConfig((prev) => {
			const newOrder = prev.column === column && prev.order === "asc" ? "desc" : "asc";
			return { column, order: newOrder };
		});
	};

	const isFilterEnabled = (filterId) => {
		switch (filterId) {
			case FILTERS_KEYS.vessels.clients:
				return canViewClients;
			case FILTERS_KEYS.vessels.distributors:
				return canViewDistributors;
			case FILTERS_KEYS.vessels.businessUnits:
				return canViewBusinessUnitFilter;
			case FILTERS_KEYS.vessels.status:
				return true;
			default:
				return false;
		}
	};

	const { dropdownFilters, isDropdownFilterVisible, isFilterVisible, toggleFilter } =
		useDropdownFilters({
			filters,
			toggleFiltersFn: toggleVesselsFiltersVisibility,
			isFilterEnabledFn: isFilterEnabled,
		});

	const handleDownloadVessels = async () => {
		if (isLoadingDownload) return;

		setIsLoadingDownload(true);
		setIsSuccess(false);
		setHasError(false);
		setErrorMessage(null);

		try {
			await setDownloadVesselsData(token, searchTerms, signal);
			setIsSuccess(true);
		} catch (error) {
			console.error("Download vessels failed:", error);
			setHasError(true);
			setErrorMessage(error?.message || "Download failed");
		} finally {
			setIsLoadingDownload(false);
		}
		setTimeout(() => {
			setIsSuccess(false);
			setHasError(false);
		}, 1500);
	};

	const addButtonLabel = useFeatureValue(FLAGS.vessels.list.addButtonLabel, "Add new vessel/site");
	const titlesConfig = useFeatureValue(FLAGS.shared.vessels.titlesConfig, {
		vessel: "Vessel/Site",
	});

	return (
		<div className="px-0 pt-20 pb-4 sm:px-2 md:px-4 lg:px-4 xl:px-4 2xl:px-4">
			{isTablet && <ModulesHeader subModuleTitle={vessel} />}

			{/* Actions and Search */}
			<div className="w-full bg-navbar-bg p-6 shadow md:rounded-lg">
				<div className="flex items-center justify-center md:justify-end">
					<>
						{canCreateVessels && (
							<Button
								text={addButtonLabel}
								icon={PlusIcon}
								onClick={() => navigate("./create")}
								className={"font-normal text-white"}
							/>
						)}

						<button
							type="button"
							onClick={handleDownloadVessels}
							disabled={isLoadingDownload || vesselsData?.length === 0}
							className={`flex items-center justify-center rounded-xl px-3 py-2 font-medium text-sm text-white transition-all duration-300 ease-in-out hover:bg-blue-gray hover:bg-opacity-40 ${
								isLoadingDownload || vesselsData?.length === 0
									? "cursor-not-allowed opacity-50"
									: ""
							}`}
						>
							<Tooltip
								content={
									hasError
										? errorMessage
										: isSuccess
											? "Downloaded!"
											: `Export ${titlesConfig.vessel} List`
								}
								className="!bg-dark-blue !bg-opacity-80 m-0 p-1 text-[11px]"
							>
								<div className="relative flex h-5 w-5 items-center justify-center">
									{/* Spinner */}
									<div
										className={`absolute transition-all duration-300 ${
											isLoadingDownload ? "scale-100 opacity-100" : "scale-50 opacity-0"
										}`}
									>
										<div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
									</div>

									{/* Success */}
									<div
										className={`absolute transition-all duration-300 ${
											isSuccess ? "scale-100 opacity-100" : "scale-50 opacity-0"
										}`}
									>
										✓
									</div>

									{/* Error */}
									<div
										className={`absolute text-red-400 transition-all duration-300 ${
											hasError ? "scale-100 opacity-100" : "scale-50 opacity-0"
										}`}
									>
										✕
									</div>

									{/* Default icon */}
									<div
										className={`transition-all duration-300 ${
											!isLoadingDownload && !isSuccess && !hasError
												? "scale-100 opacity-100"
												: "scale-50 opacity-0"
										}`}
									>
										<FileCsvIcon className="h-5 w-5 text-white" />
									</div>
								</div>
							</Tooltip>
						</button>
					</>
				</div>

				<form className="mt-4 flex flex-col items-start justify-start md:flex-row">
					{dropdownFilters && isDropdownFilterVisible && (
						<ListVesselsFilter filters={dropdownFilters} toggleFilter={toggleFilter} />
					)}

					<div className="mt-3 w-full flex-1 space-y-4 md:mt-0 md:grid md:grid-cols-2 md:gap-6 md:space-y-0 lg:grid-cols-3">
						{/* Search */}
						<SearchInput
							id="search"
							dataTestId="vouchers-search"
							placeholder={t("searchPlaceholder")}
							label={t("searchBy")}
							value={searchTerms.search}
							onChange={handleSearchChange}
							onClear={() => handleSearchChange("search", "")}
						/>

						{/* Business Unit Filter */}
						{business_units?.length !== 1 &&
							isFilterVisible(FILTERS_KEYS.vessels.businessUnits) && (
								<BusinessUnitsFilter
									value={businessUnit}
									onChange={handleBusinessUnitChange}
									enabled={business_units?.length !== 1}
								/>
							)}

						{/* Distributor Filter */}

						{canViewDistributors && isFilterVisible(FILTERS_KEYS.vessels.distributors) && (
							<DistributorsFilter
								value={distributor}
								onChange={handleDistributorChange}
								businessUnitId={businessUnit?.id}
								enabled={canViewDistributors}
							/>
						)}

						{/* Client Filter */}
						{canViewClients && isFilterVisible(FILTERS_KEYS.vessels.clients) && (
							<ClientsFilter
								value={client}
								onChange={handleClientChange}
								businessUnitId={businessUnit?.id}
								distributorId={distributor?.id}
								enabled={canViewClients}
							/>
						)}
						{isFilterVisible(FILTERS_KEYS.vessels.status) && (
							<StaticSelectFilter
								label={"Filter by"}
								placeholder={"Status"}
								showSearch={false}
								options={statusOptions}
								value={status}
								onChange={handleStatusChange}
							/>
						)}
					</div>
				</form>
			</div>

			{/* Table */}
			{loading ? (
				<div className="flex h-64 items-center justify-center">
					<div className="h-32 w-32 animate-spin rounded-full border-gray-700 border-t-2 border-b-2"></div>
				</div>
			) : (
				<div className="relative mt-4 overflow-x-auto bg-navbar-bg shadow-md md:rounded-lg">
					<ListTable
						list={vesselsData}
						columns={columns}
						onDelete={handleDeleteVessel}
						disableDeleteCondition={disableDeleteCondition}
						onEdit={(id) => navigate(`./edit/${id}`)}
						handleRowClick={(id, name, event) => handleRowClickWithCtrl(id, name, event)}
						editColumn={canEditVessels}
						deleteColumn={canDeleteVessels}
						isAction={!!canEditVessels || !!canDeleteVessels}
						fetchSortedData={fetchSortedData}
						sorted_column={sortConfig.column}
						sorted_order={sortConfig.order}
					/>
					<div className="flex flex-col items-center justify-between gap-2 bg-dark-blue bg-opacity-40 px-2 pt-4 pb-4 text-blue-gray text-xs sm:flex-row sm:gap-0 sm:px-4 sm:text-sm">
						<PageSizeSelector
							total={totalItems}
							value={localPerPage}
							startItem={startItemIndex}
							endItem={endItemIndex}
							onChange={(newSize) => {
								setLocalPerPage(newSize);
								void fetchDataVessels(
									dispatch,
									token,
									navigate,
									1,
									newSize,
									1,
									searchTerms,
									sortConfig.column,
									sortConfig.order,
								);
							}}
						/>
						<Pagination
							totalPages={totalPage}
							currentPage={currentPage}
							onPageChange={(page) =>
								fetchDataVessels(
									dispatch,
									token,
									navigate,
									page,
									localPerPage,
									1,
									searchTerms,
									sortConfig.column,
									sortConfig.order,
								)
							}
						/>
					</div>
				</div>
			)}
		</div>
	);
};

export default Vessels;
